import { useEffect, useMemo, useRef, useState } from 'react'
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material'
import EventAvailableIcon from '@mui/icons-material/EventAvailable'
import {
  aujourdhuiBoutique,
  instantDepuisHeureMurale,
  type Creneau,
  type Horaires,
  type Item,
} from '@iobiz/shared'
import { ChampDate, versIso } from '../../ui/ChampDate.js'
import { api } from '../../api/client.js'
import { useCustomers, useItems, useRendezVousDuClient } from '../../data/hooks.js'
import { useLiveQuery } from 'dexie-react-hooks'
import { creneauxDuJour, poserRendezVous } from '../../data/repositories/agenda.local.js'
import { categoriesLocales } from '../../data/repositories/catalog.local.js'
import { creerDocument } from '../../data/repositories/sales.write.js'

/**
 * Prendre un rendez-vous, au comptoir.
 *
 * ⭐ Le geste décrit par le métier tient en une boîte : le client demande une
 * prestation, la caissière regarde ce qui reste, propose, et — si le client
 * accepte — pose le créneau ET le document de vente dans le même mouvement.
 * Séparer les deux (« créez la vente, puis bloquez le créneau ») garantit qu'un
 * jour sur deux l'un des deux manquera.
 *
 * ⚠️ Seules les prestations AVEC DURÉE sont proposées : sans elle, on ne sait
 * pas quand le rendez-vous finit, donc ni quoi bloquer ni quoi proposer
 * ensuite. Le dire dans l'écran vaut mieux que de laisser chercher.
 */

type Etape = 'choix' | 'confirme'

export function PriseRendezVousDialog({
  storeId,
  jour,
  posteInitial,
  categorieInitiale,
  heureInitiale,
  horaires,
  fuseau,
  vente,
  onClose,
}: {
  storeId: string
  jour: Date
  /**
   * Le poste sur lequel on a cliqué dans la grille.
   *
   * ⭐ Cliquer « Fauteuil 2 à 14 h » doit poser le rendez-vous LÀ. Repartir
   * d'une liste vide obligerait à redésigner la place qu'on vient de montrer
   * du doigt.
   */
  posteInitial?: string | undefined
  /**
   * La catégorie de la colonne où l'on a cliqué.
   *
   * ⭐ Elle RESTREINT la liste des prestations. Cliquer le « + » de la colonne
   * « Cheveux » puis se voir proposer une pédicure n'avait pas de sens : le
   * rendez-vous se poserait sur un fauteuil de coiffure, et la place choisie
   * d'un clic serait perdue dès la première prestation d'une autre catégorie.
   *
   * ⚠️ Absente quand on passe par « Nouveau rendez-vous », qui ne désigne
   * aucune colonne : tout le catalogue est alors proposé.
   */
  categorieInitiale?: string | undefined
  /**
   * L'heure de la case cliquée, « 10:30 ».
   *
   * ⭐ Le créneau correspondant est RETENU d'avance. On a désigné une heure
   * précise dans la grille : la redemander dans une liste de vingt lignes
   * fait refaire un choix déjà fait.
   *
   * ⚠️ Seulement s'il est encore libre pour la prestation choisie — une
   * prestation de deux heures ne tient pas forcément à l'heure cliquée.
   */
  heureInitiale?: string | undefined
  horaires: Horaires
  fuseau: string
  /**
   * Une vente DÉJÀ enregistrée, à laquelle rattacher le créneau.
   *
   * ⭐ Le cas de l'oubli : la vente a été faite, le rendez-vous non. On ne
   * crée alors aucun document — en fabriquer un second ferait payer deux fois
   * la même prestation — et le client est celui de la vente, non rediscutable.
   */
  vente?: { id: string; customerId: string | null; customerName: string | null } | undefined
  onClose: () => void
}): JSX.Element {
  const clients = useCustomers(storeId, { limit: 200 })
  const articles = useItems(storeId, { limit: 300 })

  const [clientId, setClientId] = useState(vente?.customerId ?? '')
  const [itemId, setItemId] = useState('')
  const [posteId, setPosteId] = useState(posteInitial ?? '')
  const categories = useLiveQuery(() => categoriesLocales(storeId), [storeId])
  /**
   * ⚠️ Les composantes LOCALES, jamais `toISOString()`.
   *
   * Celui-ci convertit d'abord en UTC : à l'est de Greenwich, minuit du 4
   * septembre devient le 3 à 22 h, et la modale s'ouvrait sur la veille — un
   * jour souvent fermé, donc sans le moindre créneau, sans que rien ne
   * l'explique.
   */
  // ⚠️ Jamais un jour passé À LA BOUTIQUE : ouverte sur hier, la modale
  // n'offrait aucun créneau et ne disait pas pourquoi.
  const [date, setDate] = useState(() => {
    const demande = versIso(jour)
    const aujourdhui = aujourdhuiBoutique(fuseau)
    return demande < aujourdhui ? aujourdhui : demande
  })
  const [docType, setDocType] = useState<'invoice' | 'quote'>('invoice')
  const [creneaux, setCreneaux] = useState<Creneau[]>([])
  const [choisi, setChoisi] = useState<Creneau | null>(null)
  const [etape, setEtape] = useState<Etape>('choix')
  const [occupe, setOccupe] = useState(false)
  const [erreur, setErreur] = useState<string | null>(null)
  const [invitation, setInvitation] = useState<string | null>(null)

  const rdvsDuClient = useRendezVousDuClient(storeId, clientId || undefined)

  /**
   * Les prestations proposables.
   *
   * ⭐ Une prestation DÉJÀ retenue pour ce client disparaît de la liste : on ne
   * pose pas deux fois le même soin pour la même personne, et l'y laisser
   * invitait à le faire par inadvertance — deux créneaux bloqués, une seule
   * prestation vendue.
   *
   * ⚠️ Le filtre porte sur le CLIENT, pas sur la prestation en général. Une
   * manucure retenue pour Awa ne doit pas empêcher d'en poser une pour
   * quelqu'un d'autre : ce serait un salon incapable de servir deux clientes.
   *
   * ⚠️ Les rendez-vous ANNULÉS ne comptent pas : la prestation redevient
   * proposable dès qu'on libère son créneau, sans quoi une annulation
   * interdirait de reprendre le même soin.
   */
  const dejaPris = useMemo(
    () =>
      new Set(
        (rdvsDuClient ?? [])
          .filter((r) => r.status !== 'cancelled' && r.itemId)
          .map((r) => r.itemId!),
      ),
    [rdvsDuClient],
  )

  const prestations = useMemo(
    () =>
      (articles?.data ?? []).filter(
        (i) =>
          i.kind === 'service' &&
          i.durationMinutes &&
          !dejaPris.has(i.id) &&
          // La colonne cliquée commande : voir `categorieInitiale`.
          (!categorieInitiale || i.categoryId === categorieInitiale),
      ),
    [articles, dejaPris, categorieInitiale],
  )

  /** Le nom de la catégorie qui restreint la liste, s'il y en a une. */
  const nomCategorie = categorieInitiale
    ? ((categories ?? []).find((c) => c.id === categorieInitiale)?.name ?? null)
    : null
  const prestation = prestations.find((p) => p.id === itemId) ?? null
  const client = (clients?.data ?? []).find((c) => c.id === clientId) ?? null

  /**
   * Les postes de la CATÉGORIE de la prestation.
   *
   * ⚠️ Pas tous les postes de la boutique : réserver une manucure sur un
   * fauteuil de coiffure passerait la contrainte de la base et donnerait un
   * agenda incohérent, que rien ensuite ne redresserait.
   */
  const postes = useMemo(
    () => (categories ?? []).find((c) => c.id === prestation?.categoryId)?.postes ?? [],
    [categories, prestation],
  )

  /**
   * Le poste CLIQUÉ dans la grille, gardé de côté.
   *
   * ⚠️ Il ne survivait pas à l'ouverture. Tant qu'aucune prestation n'est
   * choisie, la catégorie est inconnue et `postes` est vide : l'effet
   * ci-dessous remettait alors le poste à vide — effaçant celui qu'on venait
   * de désigner — puis retombait sur le PREMIER poste dès que la prestation
   * révélait sa liste.
   *
   * Conséquence sur un créneau à moitié pris : on cliquait le « + » du second
   * fauteuil, libre, et l'écran répondait avec le premier, occupé. L'heure
   * demandée disparaissait des créneaux proposés, sans que rien dise qu'on
   * avait changé de place.
   */
  const posteSouhaite = useRef(posteInitial ?? '')

  // ⚠️ Le poste retenu doit appartenir à la prestation choisie. Changer de
  // prestation sans rouvrir ce choix laisserait un poste d'une autre catégorie,
  // et la réservation partirait sur une place qui n'existe pas pour ce service.
  useEffect(() => {
    if (postes.length === 0) {
      setPosteId('')
      return
    }
    setPosteId((actuel) => {
      if (postes.some((p) => p.id === actuel)) return actuel
      // Le poste cliqué d'abord, s'il appartient bien à cette prestation.
      const souhaite = posteSouhaite.current
      if (postes.some((p) => p.id === souhaite)) return souhaite
      return postes[0]?.id ?? ''
    })
  }, [postes])

  // Les créneaux se recalculent à chaque changement de jour ou de prestation :
  // c'est la conversation avec le client, elle ne se fige pas.
  useEffect(() => {
    if (!prestation?.durationMinutes) {
      setCreneaux([])
      return
    }
    let vivant = true
    void creneauxDuJour(
      storeId,
      date,
      horaires,
      prestation.durationMinutes,
      fuseau,
      undefined,
      // Les disponibilités sont celles du POSTE, pas de la boutique : deux
      // fauteuils libres à 14 h font deux créneaux, pas zéro.
      posteId || undefined,
    ).then((c) => {
      if (!vivant) return
      setCreneaux(c)
      // Le créneau cliqué dans la grille, s'il figure encore parmi ceux que
      // cette prestation permet. Sinon on ne présume de rien.
      setChoisi(c.find((x) => x.start === heureInitiale) ?? null)
    })
    return () => {
      vivant = false
    }
  }, [storeId, date, prestation, horaires, fuseau, posteId])

  /**
   * ⚠️ L'heure murale devient un instant avec l'horloge de la BOUTIQUE.
   *
   * `new Date('2026-09-14T14:00')` l'aurait interprétée dans le fuseau du
   * NAVIGATEUR. Tant que le poste est à l'heure de la boutique, cela ne se
   * voit pas ; dès que les deux diffèrent, le rendez-vous posé à 14 h se relit
   * à 13 h, ne recouvre plus le créneau de 14 h, et l'agenda repropose une
   * heure déjà vendue.
   */
  function instant(heure: string): string {
    return instantDepuisHeureMurale(date, heure, fuseau)
  }

  async function poser(): Promise<void> {
    if (!client || !prestation || !choisi) return
    setOccupe(true)
    setErreur(null)
    try {
      // 1. Le document — sauf s'il existe déjà : rattacher un rendez-vous
      //    oublié ne doit rien refacturer.
      const doc = vente ?? (await creerDocument(storeId, docType, {
        lines: [
          {
            itemId: prestation.id,
            label: prestation.name,
            // ⚠️ zod 4 : un champ `.optional()` reste une clé du type inféré.
            description: undefined,
            quantity: 1,
            unitPrice: prestation.salePrice,
            discountRate: 0,
            // ⚠️ Le taux de l'article AUJOURD'HUI, copié dans la ligne : une
            // prestation vendue en septembre garde son taux même si la TVA
            // change avant la date du rendez-vous.
            taxRate: prestation.taxRate,
          },
        ],
        customerId: client.id,
        customerName: client.displayName,
        currencyCode: prestation.currencyCode ?? 'XAF',
      }))

      // 2. Le créneau, rattaché au document.
      const rdv = await poserRendezVous(storeId, {
        customerId: client.id,
        itemId: prestation.id,
        transactionId: doc.id,
        label: prestation.name,
        notes: undefined,
        startsAt: instant(choisi.start),
        endsAt: instant(choisi.end),
        status: 'booked',
        // ⚠️ zod 4 : la clé existe toujours, c'est sa valeur qui peut manquer.
        // Le `...(cond ? {} : {})` d'avant ne suffit donc plus.
        posteId: posteId || undefined,
      })

      // 3. L'invitation, si le client a une adresse. Son échec ne remet RIEN en
      //    cause : le rendez-vous est pris, le document existe.
      if (client.email) {
        try {
          const r = await api.post<{ envoye: boolean; motif: string | null }>(
            `/stores/${storeId}/appointments/${rdv.id}/invitation`,
            {},
          )
          setInvitation(
            r.envoye
              ? `Invitation envoyée à ${client.email}.`
              : (r.motif ?? 'Invitation non envoyée.'),
          )
        } catch {
          // Hors ligne : l'invitation partira quand la caissière la
          // redemandera. On ne prétend pas l'avoir envoyée.
          setInvitation('Invitation non envoyée — appareil hors ligne.')
        }
      } else {
        setInvitation('Ce client n’a pas d’adresse électronique : aucune invitation envoyée.')
      }

      setEtape('confirme')
    } catch (e) {
      setErreur(e instanceof Error ? e.message : 'Enregistrement impossible.')
    } finally {
      setOccupe(false)
    }
  }

  if (etape === 'confirme') {
    return (
      <Dialog open onClose={onClose} maxWidth="xs" fullWidth>
        <DialogTitle>Rendez-vous confirmé</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 3 }}>
            {prestation?.name} pour {client?.displayName}, le{' '}
            {new Date(`${date}T12:00:00`).toLocaleDateString('fr-FR', {
              weekday: 'long',
              day: 'numeric',
              month: 'long',
            })}{' '}
            à {choisi?.start}.
          </Typography>
          {invitation && <Alert severity="info">{invitation}</Alert>}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Fermer</Button>
        </DialogActions>
      </Dialog>
    )
  }

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      {/* ⭐ La catégorie DANS LE TITRE : la liste en dessous est restreinte,
          et le dire d'emblée évite de chercher pourquoi. */}
      <DialogTitle>
        Nouveau rendez-vous{nomCategorie ? ` — ${nomCategorie}` : ''}
      </DialogTitle>
      <DialogContent dividers>
        {erreur && (
          <Alert severity="error" sx={{ mb: 3 }} role="alert">
            {erreur}
          </Alert>
        )}

        <Box sx={{ display: 'grid', gap: 3 }}>
          <Autocomplete
            options={clients?.data ?? []}
            getOptionLabel={(c) => c.displayName}
            value={client}
            onChange={(_, c) => setClientId(c?.id ?? '')}
            disabled={Boolean(vente?.customerId)}
            renderInput={(p) => <TextField {...p} label="Client" required />}
          />

          <TextField
            select
            label="Prestation"
            value={itemId}
            onChange={(e) => setItemId(e.target.value)}
            required
            helperText={
              prestations.length === 0
                ? nomCategorie
                  ? `Aucune prestation disponible dans « ${nomCategorie} ». Ouvrez « Nouveau rendez-vous » pour les autres catégories.`
                  : dejaPris.size > 0
                    ? 'Toutes les prestations de ce client ont déjà leur rendez-vous. Annulez-en un pour le reprendre.'
                    : 'Aucune prestation avec durée. Renseignez la durée d’un article de type Service.'
                : // ⚠️ On DIT que la liste est restreinte. Une prestation absente
                  // sans explication se lit comme un catalogue incomplet, et
                  // l'on va la chercher dans les réglages.
                  nomCategorie
                  ? `Services de « ${nomCategorie} », la colonne choisie. Ouvrez « Nouveau rendez-vous » pour les autres.`
                  : dejaPris.size > 0
                    ? 'Les prestations déjà retenues pour ce client ne sont pas proposées.'
                    : ' '
            }
          >
            {prestations.map((p: Item) => (
              <MenuItem key={p.id} value={p.id}>
                {p.name} — {p.durationMinutes} min
              </MenuItem>
            ))}
          </TextField>

          {/* ⭐ La PLACE, dans la catégorie de la prestation. Les créneaux
              proposés en dessous sont ceux de ce poste-là : changer de poste
              change la liste, parce que le fauteuil d'à côté peut être libre à
              l'heure où celui-ci ne l'est pas.
              ⚠️ Masqué quand la catégorie n'a pas de postes : proposer un
              choix vide laisserait croire à une panne, alors que le rendez-vous
              se prend très bien sans — à l'ancienne, un seul à la fois. */}
          {prestation && postes.length > 0 && (
            <TextField
              select
              label="Poste"
              value={posteId}
              onChange={(e) => setPosteId(e.target.value)}
              required
              helperText="Les créneaux ci-dessous sont ceux de ce poste."
            >
              {postes.map((p) => (
                <MenuItem key={p.id} value={p.id}>
                  {p.label}
                </MenuItem>
              ))}
            </TextField>
          )}

          {prestation && postes.length === 0 && (
            <Alert severity="info">
              La catégorie de cette prestation n’a aucun poste. Le rendez-vous sera pris sans
              place attribuée — ajoutez des postes dans Catalogue › Catégories pour le planning
              en colonnes.
            </Alert>
          )}

          {/* Le calendrier de l'application, comme partout ailleurs — et non
              celui du navigateur, dont l'apparence change d'un poste à
              l'autre. Borné à aujourd'hui : on ne prend pas rendez-vous hier. */}
          <ChampDate
            label="Jour"
            value={date}
            onChange={setDate}
            // ⚠️ Aujourd'hui DE LA BOUTIQUE, pas du poste : « today » est celui
            // du navigateur, qui a un jour d'avance le soir à Paris sur Bamako.
            // Un `Date`, et non « AAAA-MM-JJ », que le format « d/m/Y » du champ
            // lirait de travers.
            min={new Date(`${aujourdhuiBoutique(fuseau)}T00:00:00`)}
            // Les jours de fermeture sont grisés : inutile de les proposer
            // pour n'afficher ensuite « rien de libre ce jour-là ».
            jourFerme={(d) => horaires.days[d.getDay()]?.closed ?? false}
          />

          <Box>
            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
              Créneaux disponibles
            </Typography>
            {!prestation ? (
              <Typography variant="body2" sx={{ color: 'text.disabled' }}>
                Choisissez une prestation pour voir ce qui reste.
              </Typography>
            ) : creneaux.length === 0 ? (
              <Typography variant="body2" sx={{ color: 'text.disabled' }}>
                Rien de libre ce jour-là — boutique fermée, journée complète, ou heures déjà
                passées.
              </Typography>
            ) : (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {creneaux.map((c) => (
                  <Chip
                    key={c.start}
                    label={c.start}
                    onClick={() => setChoisi(c)}
                    color={choisi?.start === c.start ? 'primary' : 'default'}
                    variant={choisi?.start === c.start ? 'filled' : 'outlined'}
                  />
                ))}
              </Box>
            )}
          </Box>

          {/* Rien à choisir quand la vente existe : le créneau s'y rattache,
              point. Proposer « Vente ou Devis » laisserait croire qu'un second
              document va naître. */}
          {vente ? (
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              Le rendez-vous sera rattaché à cette vente.
            </Typography>
          ) : (
            <TextField
              select
              label="Document"
              value={docType}
              onChange={(e) => setDocType(e.target.value as 'invoice' | 'quote')}
              helperText="Le rendez-vous y sera rattaché."
            >
              <MenuItem value="invoice">Vente</MenuItem>
              <MenuItem value="quote">Devis</MenuItem>
            </TextField>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 6, py: 4 }}>
        <Button onClick={onClose}>Annuler</Button>
        <Button
          variant="contained"
          startIcon={<EventAvailableIcon />}
          disabled={!client || !prestation || !choisi || occupe}
          onClick={() => void poser()}
        >
          Bloquer le créneau
        </Button>
      </DialogActions>
    </Dialog>
  )
}
